import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/api';
import { User, Eye } from 'lucide-react';
import BlockButton from './BlockButton';

// Helper function to get default avatar based on gender
const getDefaultAvatar = (gender) => {
  if (gender === 'female' || gender === 'Mujer') {
    return '/images/avatar_mujer.webp';
  } else if (gender === 'male' || gender === 'Hombre') {
    return '/images/avatar_hombre.jpg';
  }
  // Default fallback
  return '/images/default-avatar.svg';
};

const ChatWindow = ({ conversation, currentUser, onBack, onNewMessage }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [isUserBlocked, setIsUserBlocked] = useState(false);

  // Manejar cambios de bloqueo
  const handleBlockChange = (userId, isBlocked) => {
    setIsUserBlocked(isBlocked);
    if (isBlocked) {
      // Si el usuario es bloqueado, cerrar el chat y volver a la lista
      onBack();
    }
  };

  // Cargar mensajes
  const loadMessages = async (pageNum = 1, append = false) => {
    try {
      if (!append) setLoading(true);
      
      // Cargar solo 10 mensajes por página para mejor rendimiento
      const limit = 10;
      const response = await apiService.getMessages(conversation.id, pageNum, limit);
      const newMessages = response.messages || [];
      
      if (append) {
        // Para mensajes antiguos, guardar la posición del scroll ANTES de agregar mensajes
        const container = messagesContainerRef.current;
        const scrollHeight = container.scrollHeight;
        const scrollTop = container.scrollTop;
        
        setMessages(prev => [...newMessages, ...prev]);
        
        // DESPUÉS de que React actualice el DOM, ajustar el scroll
        setTimeout(() => {
          const newScrollHeight = container.scrollHeight;
          const heightDifference = newScrollHeight - scrollHeight;
          container.scrollTop = scrollTop + heightDifference;
        }, 0);
      } else {
        setMessages(newMessages);
        // Scroll al final solo si es la primera carga
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
      
      setHasMore(newMessages.length === limit);
      setError('');
    } catch (err) {
      setError('Error al cargar los mensajes');
    } finally {
      setLoading(false);
    }
  };

  // Enviar mensaje
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);
    setIsTyping(false);

    try {
      const response = await apiService.sendMessage(conversation.id, messageText);
      
      // Agregar el mensaje enviado a la lista local
      if (response.data) {
        setMessages(prev => [...prev, response.data]);
        scrollToBottom();
        onNewMessage(response.data);
      }
      
      setError('');
    } catch (err) {
      const errorMsg = 'Error al enviar el mensaje';
      setError(errorMsg);
      setNewMessage(messageText); // Restaurar el mensaje
    } finally {
      setSending(false);
    }
  };

  // Detectar escritura
  const handleTyping = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    
    // Mostrar indicador de escritura
    if (value.trim()) {
      setIsTyping(true);
      
      // Limpiar timeout anterior
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
      
      // Ocultar indicador después de 2 segundos sin escribir
      const timeout = setTimeout(() => {
        setIsTyping(false);
      }, 2000);
      
      setTypingTimeout(timeout);
    } else {
      setIsTyping(false);
    }
  };

  // Scroll al final
  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  // Cargar más mensajes (scroll hacia arriba)
  const loadMoreMessages = async () => {
    if (!hasMore || loading || loadingMore) return;
    
    setLoadingMore(true);
    const nextPage = page + 1;
    setPage(nextPage);
    await loadMessages(nextPage, true);
    setLoadingMore(false);
  };

  // Detectar scroll hacia arriba para cargar más mensajes
  const handleScroll = (e) => {
    const { scrollTop } = e.target;
    if (scrollTop === 0 && hasMore && !loading && !loadingMore) {
      loadMoreMessages();
    }
  };

  useEffect(() => {
    if (conversation) {
      setMessages([]);
      setPage(1);
      setHasMore(true);
      loadMessages();
      
      // Polling cada 10 segundos para nuevos mensajes
      // Solo agregar mensajes nuevos sin afectar el scroll si el usuario no está al final
      const interval = setInterval(async () => {
        try {
          const container = messagesContainerRef.current;
          const isAtBottom = container && 
            (container.scrollHeight - container.scrollTop - container.clientHeight) < 100;
          
          const response = await apiService.getMessages(conversation.id, 1, 10);
          const latestMessages = response.messages || [];
          
          setMessages(prevMessages => {
            const lastMessageId = prevMessages.length > 0 ? prevMessages[prevMessages.length - 1].id : null;
            const newMessages = latestMessages.filter(msg => 
              !prevMessages.some(prevMsg => prevMsg.id === msg.id)
            );
            
            if (newMessages.length > 0) {
              const updatedMessages = [...prevMessages, ...newMessages];
              
              // Solo hacer scroll automático si estaba al final
              if (isAtBottom) {
                setTimeout(scrollToBottom, 100);
              }
              
              return updatedMessages;
            }
            
            return prevMessages;
          });
        } catch (err) {
        }
      }, 10000);
      
      return () => clearInterval(interval);
    }
  }, [conversation]);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${hours}:${minutes < 10 ? '0' + minutes : minutes}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Agrupar mensajes por fecha
  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach(message => {
      const date = new Date(message.created_at).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    return groups;
  };

  // Limitar el número de mensajes renderizados para mejorar el rendimiento
  const MAX_RENDERED_MESSAGES = 100;
  const messagesToRender = messages.length > MAX_RENDERED_MESSAGES 
    ? messages.slice(-MAX_RENDERED_MESSAGES) 
    : messages;
  
  const messageGroups = groupMessagesByDate(messagesToRender);

  return (
    <div className="flex flex-col h-96 md:h-[600px]">
      {/* Header del chat */}
      <div className="flex items-center p-4 border-b border-gray-200 bg-gray-50">
        <button
          onClick={onBack}
          className="mr-3 text-pink-600 hover:text-pink-700 hover:scale-110 active:scale-95 transition-all duration-200 text-xl font-bold hover:bg-pink-50 p-1 rounded-full"
        >
          ←
        </button>
        
        <Link 
          to={`/perfil/${conversation.id}`}
          className="relative mr-3 hover:scale-110 transition-transform duration-200"
          title="Ver perfil completo"
        >
          <img
            src={conversation.profile_picture || getDefaultAvatar(conversation.gender)}
            alt={conversation.first_name}
            className="w-10 h-10 rounded-full object-cover cursor-pointer"
            onError={(e) => {
              e.target.src = getDefaultAvatar(conversation.gender);
            }}
          />
          {conversation.is_online && (
            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
          )}
        </Link>
        
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900">
            {conversation.first_name}
          </h3>
          <p className="text-sm text-gray-600">
            @{conversation.username}
            {conversation.is_online && (
              <span className="text-green-600 ml-2">● En línea</span>
            )}
            {isTyping && (
              <span className="text-blue-600 ml-2 animate-pulse">✏️ Escribiendo...</span>
            )}
          </p>
        </div>

        {/* Botón de bloqueo */}
        <div className="ml-3">
          <BlockButton
            targetUser={{
              id: conversation.id,
              name: conversation.first_name,
              username: conversation.username
            }}
            onBlockChange={handleBlockChange}
            size="sm"
          />
        </div>
        
        {/* Botón para ver perfil */}
        <Link
          to={`/perfil/${conversation.id}`}
          className="flex items-center space-x-2 px-3 py-2 text-pink-600 hover:text-pink-700 hover:bg-pink-50 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
          title="Ver perfil completo"
        >
          <Eye className="h-4 w-4" />
          <span className="text-sm font-medium">Ver Perfil</span>
        </Link>
      </div>

      {/* Área de mensajes */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4"
        onScroll={handleScroll}
      >
        {loading && page === 1 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mb-4"></div>
            <p className="text-gray-600 text-sm">Cargando mensajes...</p>
            <p className="text-gray-400 text-xs mt-1">Esto puede tomar un momento si hay muchos mensajes</p>
          </div>
        ) : (
          <>
            {hasMore && (
              <div className="text-center py-4">
                <div className="text-xs text-gray-500 mb-3">
                  Mostrando {messagesToRender.length} de {messages.length} mensajes
                </div>
                <button
                  onClick={loadMoreMessages}
                  className="bg-pink-600 text-white px-6 py-3 rounded-lg hover:bg-pink-700 hover:scale-105 active:scale-95 transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || loadingMore}
                >
                  {loadingMore ? (
                    <span className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Cargando más mensajes...
                    </span>
                  ) : (
                    '📜 Cargar 10 mensajes anteriores'
                  )}
                </button>
              </div>
            )}
            
            {Object.entries(messageGroups).map(([date, dayMessages]) => (
              <div key={date}>
                {/* Separador de fecha */}
                <div className="flex justify-center mb-4">
                  <span className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                    {formatDate(dayMessages[0].created_at)}
                  </span>
                </div>
                
                {/* Mensajes del día */}
                {dayMessages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.sender_id === currentUser.id 
                        ? 'justify-end' 
                        : 'justify-start'
                    } mb-2`}
                  >
                    <div
                      className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        message.sender_id === currentUser.id
                          ? 'bg-pink-600 text-white'
                          : 'bg-gray-200 text-gray-900'
                      }`}
                    >
                      <p className="break-words">{message.message}</p>
                      <p
                        className={`text-xs mt-1 ${
                          message.sender_id === currentUser.id
                            ? 'text-pink-100'
                            : 'text-gray-500'
                        }`}
                      >
                        {formatTime(message.created_at)}
                        {message.sender_id === currentUser.id && message.is_read && (
                          <span className="ml-1">✓✓</span>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Error message */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-t border-red-200">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Input para enviar mensajes */}
      <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200">
        <div className="flex space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={handleTyping}
            placeholder="Escribe tu mensaje..."
            className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-pink-500 transition-all duration-200"
            maxLength={1000}
            disabled={sending}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(e);
              }
            }}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="bg-pink-600 text-white px-6 py-2 rounded-lg hover:bg-pink-700 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
          >
            {sending ? '...' : '➤'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {newMessage.length}/1000 caracteres
        </p>
      </form>
    </div>
  );
};

export default ChatWindow;